import { useEffect, useMemo, useRef, useState } from 'react';
import { BuyDialog } from './BuyDialog/BuyDialog';
import { ProductTypesButtonGroup } from './ProductTypesButtonGroup';
import { ProductSwiper } from 'components/ProductSwiper/ProductSwiper';
import { PriceTag } from './common/PriceTag';
import { Link } from 'react-router-dom';
import routeNames from 'router/routeNames.json';
import { productTypes } from 'constants';
import { showSweaters } from 'helpers/uglySweaters';
import useImageUrl from 'hooks/useImageUrl';

export const HomeTee = ({ tee, isLoading }) => {
  const [page, setPage] = useState(0);
  const [initialPage, setInitialPage] = useState();
  const swiperRef = useRef();
  const constructImageUrl = useImageUrl();

  const isTeeAvailable = tee?.types.length > 0;
  const teeTypes = useMemo(() => {
    if (isTeeAvailable && !isLoading) {
      const types = tee.types.map((item) => ({
        ...item,
        imgUrl: constructImageUrl({
          img: item.img,
          width: 450,
          height: 540,
        }),
      }));

      return [
        types[0],
        {
          id: 'zoom',
          name: 'zoom',
          img: tee.zoom,
          price: types[0]?.price,
          imgUrl: constructImageUrl({
            img: tee.zoom,
            width: 450,
            height: 540,
          }),
        },
        ...types.slice(1),
      ];
    } else {
      return [];
    }
  }, [constructImageUrl, isLoading, isTeeAvailable, tee?.types, tee?.zoom]);

  const activeType = teeTypes[page];

  const handlePageChange = (nextPage) => {
    swiperRef.current.swiper.slideTo(nextPage + 1);
    setPage(nextPage);
  };

  useEffect(() => {
    if (teeTypes.length > 0) {
      if (showSweaters) {
        const sweaterIndex = teeTypes.findIndex(
          (type) => type.name === productTypes.sweater
        );
        const validIndex = sweaterIndex !== -1 ? sweaterIndex : 0;
        setPage(validIndex);
        setInitialPage(validIndex);
      } else {
        setInitialPage(0);
      }
    }
  }, [teeTypes]);

  return (
    <div className="[max-width:500px] w-full md:w-1/3 mb-4 md:mb-0">
      <div className="w-full">
        <header className="flex justify-between bg-qw-tee-black text-white h-11">
          {!isLoading && (
            <>
              <div className="px-1.5 md:py-2 truncate flex md:items-center">
                <div className="truncate tracking-tightest flex items-center">
                  <h3 className="text-white font-bold uppercase font-sans text-xl md:text-base lg:text-xl leading-8 m-0">
                    {tee?.name}
                  </h3>
                  <span className="italic hidden md:inline md:text-xs lg:text-sm ml-1 self-end">
                    {' '}
                    by{' '}
                    <Link
                      className="text-white hover:text-qw-green font-normal"
                      to={`${routeNames.profile}/${tee.user?.id}`}
                    >
                      {tee?.user?.username}
                    </Link>
                  </span>
                </div>
              </div>

              <PriceTag price={activeType?.price} size="medium" />
            </>
          )}
        </header>
      </div>

      <div className="w-full h-full">
        <ProductSwiper
          ref={swiperRef}
          teeTypes={teeTypes}
          teeName={tee?.name}
          initialSlide={initialPage}
          isTeeAvailable={isTeeAvailable}
          isLoading={isLoading || initialPage == null}
          imgHeight={540}
          imgWidth={450}
          onSlideChange={(swiper) => setPage(swiper.realIndex)}
        />
      </div>

      <div className="w-full mx-auto">
        <footer className="relative bg-qw-tee-gray flex justify-between items-end py-1 md:py-0 h-[70px] px-0 md:px-1 lg:px-0">
          {isTeeAvailable && (
            <>
              <ProductTypesButtonGroup
                active={page}
                onChange={handlePageChange}
                types={teeTypes}
                className="overflow-x-hidden hidden lg:flex"
              />

              <BuyDialog product={tee}>
                <button className="px-2 flex-shrink-0 rounded-2xl bg-qw-blue-500 text-white font-bold font-sans text-3xl text-shadow tracking-tighter active:bg-qw-green hover:bg-qw-green w-full lg:w-auto h-[52px] lg:h-[70px]">
                  Buy Now
                </button>
              </BuyDialog>
            </>
          )}
        </footer>
      </div>
    </div>
  );
};
